/**
 * Mail-domain UI primitives shared across Phase 2b screens.
 *
 * - `KPICard` mirrors `screens/src/components/KPICard.jsx`.
 * - `FilterBar` is a small wrapper around a search input + dropdown filters,
 *   sufficient for the list screens (Drafts, Items, Tracking, Damages, etc.).
 * - `MailItemTable` renders a styled table with the columns the screens
 *   prototype uses for mail items (tracking, sender, receiver, status, action).
 *
 * These live in features/mail/shared/ for now. Once libs/ui ships its own
 * data-table component, we collapse this into a thin re-export.
 */
import type { JSX, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { type LucideIcon, Search } from 'lucide-react';

import { StatusBadge } from '../../shared/StaffUI';
import type { MailItemSummary } from '../../../lib/api/mail-items';

interface KPICardProps {
  readonly title: string;
  readonly value: string;
  readonly hint?: string;
  readonly icon?: LucideIcon;
  readonly tone?: 'default' | 'success' | 'warning' | 'danger';
}

export function KPICard({ title, value, hint, icon: Icon, tone = 'default' }: KPICardProps): JSX.Element {
  const toneClass: Record<NonNullable<KPICardProps['tone']>, string> = {
    default: 'text-gray-900',
    success: 'text-nipost-green',
    warning: 'text-amber-700',
    danger: 'text-red-600',
  };
  return (
    <div className="bg-white border border-border rounded-xl p-5">
      <div className="flex items-center justify-between">
        <p className="text-[10px] font-semibold text-muted uppercase tracking-widest">{title}</p>
        {Icon ? <Icon className="w-4 h-4 text-muted" /> : null}
      </div>
      <p className={`text-2xl font-bold mt-2 leading-none ${toneClass[tone]}`}>{value}</p>
      {hint ? <p className="text-[11px] text-muted mt-1">{hint}</p> : null}
    </div>
  );
}

interface FilterBarProps {
  readonly searchValue: string;
  readonly onSearchChange: (value: string) => void;
  readonly searchPlaceholder?: string;
  readonly children?: ReactNode;
}

export function FilterBar({
  searchValue,
  onSearchChange,
  searchPlaceholder = 'Search…',
  children,
}: FilterBarProps): JSX.Element {
  return (
    <div className="flex flex-wrap items-center gap-3 mb-4">
      <div className="relative flex-1 min-w-[240px]">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted" />
        <input
          type="search"
          value={searchValue}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder={searchPlaceholder}
          aria-label="Search"
          className="w-full pl-9 pr-3 py-2 text-sm border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-nipost-green/20 focus:border-nipost-green"
        />
      </div>
      {children}
    </div>
  );
}

interface MailItemTableProps {
  readonly items: readonly MailItemSummary[];
  readonly emptyMessage?: string;
}

export function MailItemTable({ items, emptyMessage = 'No mail items.' }: MailItemTableProps): JSX.Element {
  if (items.length === 0) {
    return (
      <div className="bg-white border border-border rounded-xl p-8 text-center text-sm text-muted">
        {emptyMessage}
      </div>
    );
  }
  return (
    <div className="bg-white border border-border rounded-xl overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-muted">
          <tr>
            <th className="text-left px-4 py-2">Tracking</th>
            <th className="text-left px-4 py-2">Service</th>
            <th className="text-left px-4 py-2">Status</th>
            <th className="text-left px-4 py-2">Operator</th>
            <th className="text-left px-4 py-2">Registered</th>
            <th className="text-right px-4 py-2"></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {items.map((m) => (
            <tr key={m.id} className="hover:bg-gray-50">
              <td className="px-4 py-2 font-mono text-xs font-bold text-gray-900">
                {m.trackingNumber}
              </td>
              <td className="px-4 py-2 text-xs text-gray-700">{m.serviceClass}</td>
              <td className="px-4 py-2"><StatusBadge status={m.status} /></td>
              <td className="px-4 py-2 text-xs text-muted">#{m.operatorId}</td>
              <td className="px-4 py-2 text-xs text-muted">
                {new Date(m.registeredAt).toLocaleString()}
              </td>
              <td className="px-4 py-2 text-right">
                <Link
                  to={`/mail/items/${encodeURIComponent(m.id)}`}
                  className="text-xs font-semibold text-nipost-green hover:underline"
                >
                  Open →
                </Link>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function formatNaira(amountMinor: number): string {
  // Amount is stored in minor units (kobo). Divide by 100 for display.
  // Use a manual formatter to keep tests deterministic across locales (jsdom
  // defaults to en-US which renders NGN as "NGN" rather than "₦").
  const major = Math.round(amountMinor / 100);
  return `₦${major.toLocaleString('en-US')}`;
}
